import { Component, Input, NgZone, OnInit } from '@angular/core';
import { ColDef, ColumnResizedEvent, ColumnVisibleEvent, GridApi, GridOptions, GridReadyEvent, GridSizeChangedEvent, ValueGetterParams } from 'ag-grid-community';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { ModuleManager } from 'src/shared/models/module-manager';
import { ModuleManagerRefreshType } from 'src/shared/models/module-manager/module-manager-options.model';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { CxoneDispositionsService } from './cxone-dispositions.service';
import { CxoneDispostions, ModuleView } from './cxone-dispositions.model';
import { IModuleManagerMenuItem, ModuleManagerMenuItemType, ModuleManagerSubMenu } from 'src/shared/models/module-manager/module-manager-menu-item.model';
import Highcharts from 'highcharts';
import HC_more from "highcharts/highcharts-more";

@Component({
    selector: 'cxone-dispositions',
    templateUrl: './cxone-dispositions.component.html',
    styleUrl: './cxone-dispositions.component.scss'
})
export class CxoneDispositionsComponent implements OnInit {
    @Input() moduleKey: string;
    @Input() moduleManager: ModuleManager;

    public showFilter = false;
    gridOptions: GridOptions;
    dataLoadSuccess = false;
    filterValue = '';
    noData = false;
    //For cxone search placeholder localization
    searchPlaceholder = "";
    isAgentStateDetailModalOpen = false;

    public data: CxoneDispostions[] = [];
    public tableState: any;
    public params: any;
    public stateRestoreInProgress = false; // Flag to prevent state save while restoring state until the widget is "stable"
    public stateSaveTimer: any;
    public isFirstLoad = true; // used for logging in the LoadData()
    public isDataLoading = false;
    public gridApi: GridApi;
    public singleMode = true;
    public dualMode = false;
    public highcharts = Highcharts;
    public labelName: string[];
    public dataForChart: number[] = [];
    private highChartObject: Highcharts.Chart = null;
    public chartConfig: Highcharts.Options;
    public total: number = 0;
    public dynamicColors: { [key: string]: string } = {};
    public fixedOtherColor = { Others: '#9DA953' };
    public dispositionColors = [
        '#99CABD',
        '#4959A4',
        '#9F8BC7',
        '#6A46A4',
        '#4F8192'
    ];
    public legendResizeValues = {
        width: '90%',
        maxHeight: 70
    };
    //State Management
    moduleState = {
        name: 'dispositionsState',
        autoLoad: true,
        initialLoad: false,
        view: ModuleView.Table,
        legendPosition: 'bottom'
    };
    setTableMode: boolean;

    constructor(
        private cvLocalizationService: CVLocalizationService,
        private agGridHelperService: AgGridHelperService,
        private dispositionsService: CxoneDispositionsService,
        public config: CvConfigurationService,
        public zone: NgZone
    ) { }

    private get isResizeWaiting() { return this.moduleManager?.container.isResizeWaiting; };

    async ngOnInit() {
        HC_more(this.highcharts);
        await Promise.all([
            this.cvLocalizationService.GetSection('Dispositions_List'),
            this.cvLocalizationService.GetSection('Error_Module'),
            this.cvLocalizationService.GetSection('Common'),
        ]);

        this.moduleManager.setOptions({
            name: 'Dispositions', // sets the title of the module (defaults to the database entry ModuleName if unset)
            onSettingsApplied: () => this.loadData(false),
            hasRefresh: true,
            refreshType: ModuleManagerRefreshType.FourHour,
            onRefresh: (isAutoRefresh: boolean) => this.loadData(isAutoRefresh),
            onExport: (format) => this.zone.run(() => this.dispositionsService.LoadDataForExport(this.moduleKey, format)),
            canRefresh: () => !this.isDataLoading, // Disable Refresh when there's already a data load in progress.
        });

        this.searchPlaceholder = this.cvLocalizationService.getCachedText("Common", "Search", "Search");

        this.moduleManager.addHeaderItem({
            name: "Search",
            icon: "search",
            click: () => this.toggleOpenSearch(),
        });

        this.gridOptions = this.agGridHelperService.createGridOptions({
            defaultColDef: {
                sortable: true,
                filter: true,
                lockPinned: false,
                resizable: true,
                suppressMenu: false,
                enableValue: true,
                cellStyle: { textAlign: 'right' },
                headerClass: 'ag-right-aligned-header',
                minWidth: this.agGridHelperService.defaultMinWidth,
            },
            context: { moduleManager: this.moduleManager },
            // grid options
            accentedSort: true,
            animateRows: true,
            suppressCellFocus: true,
            suppressScrollOnNewData: true,
            suppressContextMenu: false,
            // grid events
            onGridReady: this.onGridReady.bind(this),
            onColumnVisible: this.onColumnVisible.bind(this),
            onFilterChanged: () => {
                this.updateTableState();
                this.displayTableTotal();
            },
            onFirstDataRendered: (event) => {
                this.displayTableTotal();
                this.menuItemsForTable();
            },
            onSortChanged: () => this.updateTableState(),
            onColumnMoved: () => this.updateTableState(),
            onColumnPinned: () => this.updateTableState(),
            onColumnResized: this.onColumnResized.bind(this),
            onCellClicked: (params: any) => this.params = params,
            onGridSizeChanged: this.onGridSizeChanged.bind(this),
            // grid data
            columnDefs: [
                {
                    field: 'Name',
                    headerName: this.cvLocalizationService.getCachedText('Dispositions_List', 'Disposition', 'Disposition'),
                    filter: 'agTextColumnFilter',
                    cellClass: 'dispositions-cell',
                    headerClass: 'dispositions-header',
                    cellStyle: { textAlign: 'left' }
                },
                {
                    field: 'Percentage',
                    headerName: this.cvLocalizationService.getCachedText('Dispositions_List', 'Disposition_Percentage', 'Disposition %'),
                    filter: 'agTextColumnFilter',
                    valueGetter: this.valueGetterForColumns.bind(this)
                },
                {
                    field: 'Handled',
                    headerName: this.cvLocalizationService.getCachedText('Dispositions_List', 'Handled', 'Handled'),
                    filter: 'agTextColumnFilter'
                },
                {
                    field: "AHT",
                    headerName: this.cvLocalizationService.getCachedText('Dispositions_List', 'AHT', 'AHT'),
                    filter: 'agTextColumnFilter'
                }
            ],
            rowData: [],
            overlayNoRowsTemplate: `<div class="error-container" style="padding-top: 48px;">
                    <svg class="mb-2">
                        <use href="../App_Themes/CXone/icons/no-results.svg#no-results"></use>
                    </svg>
                    <p class="cxone-error-text">
                        ${this.cvLocalizationService.getCachedText("Error_Module", "CXOne_No_Results", "No results found")}
                    </p>
                </div>`,
            localeText: await this.agGridHelperService.getLocalizations(),
            suppressDragLeaveHidesColumns: true,
            suppressNoRowsOverlay: true,
        }, { enableCopy: true });

        this.moduleManager.addState({
            name: "tableState",
            getStateData: () => this.tableState,
            onStateLoaded: (stateData) => {
                if (!stateData) { // No table state loaded
                    return;
                }

                this.tableState = stateData;
                this.restoreColumnState();
            },
            autoLoad: true
        });

        this.moduleManager.addState({
            name: 'dispositionsState',
            autoLoad: true,
            getStateData: () => this.moduleState,
            onStateLoaded: stateData => {
                if (stateData) {
                    this.handleOnStateLoaded(stateData);
                }
            }
        });

        this.moduleManager.registerEvents({
            onResizeStop: () => {
                window.setTimeout(() => {
                    if (this.highChartObject && this.moduleState.view.includes('chart')) {
                        this.highChartObject?.reflow();
                    }
                    this.resizeLegends();
                }, 500);
            },
        });

        this.setComboMenu();
        this.loadData(false);
    }

    valueGetterForColumns(params: ValueGetterParams) {
        if (params.node.rowPinned && params.node.rowPinned === 'bottom') {
            return `${(params.data.Percentage).toFixed(2)}%`;
        } else {
            return params.data?.Percentage ? `${(params.data.Percentage * 100).toFixed(2)}%` : "0.00%";
        }
    }

    setChartInstance(chartInstance: Highcharts.Chart) {
        this.highChartObject = chartInstance;

        if (this.moduleState.view === ModuleView.Chart || this.moduleState.view === ModuleView.Table_and_Chart) {
            this.menuItemsForChart();
        }

        if (this.highChartObject) {
            this.highChartObject?.reflow();
        }
    }

    handleOnStateLoaded(stateData) {
        if (!stateData) {
            this.setModuleState(ModuleView.Table);
            return;
        }
        this.moduleState = stateData;
        if (this.moduleState.view) {
            this.setModuleState(this.moduleState.view);
        } else {
            this.setModuleState(ModuleView.Chart);
        }

        if (this.moduleState.view === ModuleView.Table || this.moduleState.view === ModuleView.Table_and_Chart) {
            this.menuItemsForTable();
        }
        this.moduleState.initialLoad = true;
    }

    setComboMenu() {
        const displayItems = [];
        const options = this.moduleManager.getModuleInfo().options;
        if (!options?.tableAvailable || !options?.chartAvailable)
            return;

        if (options.tableAvailable) {
            displayItems.push({
                name: this.cvLocalizationService.getCachedText('Dispositions_List', 'Table', 'Table'),
                icon: 'tableView',
                getEnabled: () => true,
                click: () => {
                    this.setTableMode = true;
                    this.setModuleState(ModuleView.Table);
                }
            });
        }
        if (options.chartAvailable) {
            displayItems.push({
                name: this.cvLocalizationService.getCachedText('Dispositions_List', 'Chart', 'Chart'),
                icon: 'chartView',
                getEnabled: () => true,
                click: () => {
                    this.setModuleState(ModuleView.Chart);
                }
            });
        }
        if (options.tableAndChartAvailable) {
            displayItems.push({
                name: this.cvLocalizationService.getCachedText('Dispositions_List', 'Table_and_Chart', 'Table and Chart'),
                icon: 'tableAndChartView',
                getEnabled: () => true,
                click: () => {
                    this.setTableMode = true;
                    this.setModuleState(ModuleView.Table_and_Chart);
                }
            });
        }
        this.moduleManager.addMenuItem({
            name: this.cvLocalizationService.getCachedText('Dispositions_List', 'Widget_Display', 'Widget Display'),
            icon: 'chartType',
            type: ModuleManagerMenuItemType.SubMenu,
            getEnabled: () => this.dataLoadSuccess,
            subItems: displayItems,
            readOnlyEnabled: true
        } as ModuleManagerSubMenu);
    }

    setModuleState(state) {
        if (this.moduleState.view === state) {
            return;
        }
        this.moduleManager.resetMenu();
        this.setComboMenu();
        this.moduleState.view = state;

        switch (this.moduleState.view) {
            case (ModuleView.Table):
                this.singleMode = true;
                this.dualMode = false;
                this.moduleState.view = ModuleView.Table;
                this.menuItemsForTable();
                break;
            case (ModuleView.Chart):
                this.singleMode = true;
                this.dualMode = false;
                this.moduleState.view = ModuleView.Chart;
                this.menuItemsForChart();
                break;
            case (ModuleView.Table_and_Chart):
                this.singleMode = false;
                this.dualMode = true;
                this.moduleState.view = ModuleView.Table_and_Chart;
                this.menuItemsForChart();
                this.menuItemsForTable();
                break;
            default:
                this.singleMode = true;
                this.dualMode = false;
                break;
        }

        if (this.highChartObject.legend)
            this.resizeLegends();
        this.moduleManager.saveState('dispositionsState');
        this.moduleManager.requestPublishNeeded();
    }

    menuItemsForChart() {
        this.moduleManager.addMenuItem({
            name: this.cvLocalizationService.getCachedText('Dispositions_List', 'Legend_Position'),
            icon: 'legend',
            type: ModuleManagerMenuItemType.SubMenu,
            getEnabled: () => this.highChartObject != null && !this.noData,
            subItems: [{
                name: this.cvLocalizationService.getCachedText('Dispositions_List', 'Top'),
                icon: 'positionTop',
                click: () => this.setLegendPosition('top', true),
            },
            {
                name: this.cvLocalizationService.getCachedText('Dispositions_List', 'Bottom'),
                icon: 'positionBottom',
                click: () => this.setLegendPosition('bottom', true),
            },
            {
                name: this.cvLocalizationService.getCachedText('Dispositions_List', 'Left'),
                icon: 'positionLeft',
                click: () => this.setLegendPosition('left', true),
            },
            {
                name: this.cvLocalizationService.getCachedText('Dispositions_List', 'Right'),
                icon: 'positionRight',
                click: () => this.setLegendPosition('right', true),
            }],
            readOnlyEnabled: true
        } as ModuleManagerSubMenu);

        this.setLegendPosition(this.moduleState.legendPosition, false);
    }

    async setLegendPosition(position, savePosition) {
        this.moduleState.legendPosition = position;
        if (this.highChartObject) {
            this.highChartObject.update({ legend: this.getLegendConfig(position) });
        }

        this.resizeLegends();

        if (savePosition) {
            await this.moduleManager.saveState('dispositionsState');
            this.moduleManager.requestPublishNeeded();
        }
    }

    menuItemsForTable() {
        this.moduleManager.addMenuItem({
            name: this.cvLocalizationService.getCachedText("Dispositions_List", "Columns", "Columns"),
            icon: "columns",
            getEnabled: () => this.gridOptions.columnDefs.length > 0,
            type: ModuleManagerMenuItemType.CheckMenu,
            getCheckItems: () => this.gridOptions.columnDefs,
            getItemName: i => {
                const colState = this.gridOptions.columnDefs[i] as ColDef;
                const column = this.gridApi.getColumn(colState.field);

                if (column)
                    return column['colDef'].headerName;
                else
                    return "";
            },
            isItemChecked: i => {
                if (!this.tableState) {
                    this.prepareTableState();
                }
                const columnDefinition: ColDef = this.gridOptions.columnDefs[i];

                const tableStateMatch = this.tableState.columns.find(c => c.name === columnDefinition.field);
                if (tableStateMatch) {
                    columnDefinition.hide = !tableStateMatch.visible;
                }
                return !columnDefinition.hide;
            },
            onCheckClicked: i => this.toggleColumnVisible(i),
        } as IModuleManagerMenuItem);
    }

    toggleColumnVisible(i) {
        const column: ColDef = this.gridOptions.columnDefs[i];
        const tableStateMatch = this.tableState.columns.find(c => c.name === column.field);
        if (tableStateMatch) {
            column.hide = !tableStateMatch.visible;
        }
        const isHidden = !tableStateMatch.visible;
        this.gridApi.setColumnVisible(tableStateMatch.name, isHidden);
        this.prepareTableState();
        this.queueStateSave();
    }

    prepareTableState() {
        if (!this.gridApi)
            return;

        const tableState = this.agGridHelperService.getGridState(this.gridApi);
        if (tableState) {
            this.tableState = tableState;
        }
    }

    onColumnResized(event: ColumnResizedEvent) {
        if (event.source == "uiColumnResized" || (event.source == "autosizeColumns" && !this.moduleManager.container.isTogglingFullscreen)) {
            this.updateTableState();
        }
    }

    onColumnVisible(event: ColumnVisibleEvent) {
        if (this.tableState == null || this.tableState.columns.map(c => c.width).filter(w => w != null).length === 0)
            this.agGridHelperService.sizeColumnsSmart(event);
        this.updateTableState();
    }

    onGridSizeChanged(event: GridSizeChangedEvent) {
        if (this.isResizeWaiting) {
            this.agGridHelperService.sizeColumnsSmart(event);
            this.queueStateSave();
        } else if (this.setTableMode) {
            this.setTableMode = false;
            this.agGridHelperService.sizeColumnsSmart(event);
            this.prepareTableState();
            this.queueStateSave();
        } else if (this.moduleManager.container.isFullscreen) {
            this.agGridHelperService.sizeColumnsSmart(event);
        } else {
            this.restoreColumnState();
        }
    }

    onGridReady(event: GridReadyEvent) {
        this.gridApi = event.api;
        if (this.data) {
            event.api.setGridOption('rowData', this.data);
        }

        this.stateRestoreInProgress = true; // Prevent saving while the widget is still loading the initial states

        this.restoreColumnState(); //Needed to restore columns size saved on the tableState.
        if (!this.tableState) {
            this.agGridHelperService.sizeColumnsSmart(event); //This will trigger the publish bar only once if the gridState is null.
            this.updateTableState();
        }

        setTimeout(() => this.stateRestoreInProgress = false, 0); // Wait for the next tick to let events finish before allowing state save
    }

    public applyFilter() {
        if (this.gridApi) { // notify ag-grid that the filter changed
            this.gridApi.setGridOption('quickFilterText', this.filterValue);
            if (this.showFilter && this.gridApi?.getDisplayedRowCount() === 0 && this.filterValue.length !== 0)
                this.gridApi?.showNoRowsOverlay();
            else
                this.gridApi?.hideOverlay();
        }
    }

    public clearFilter() {
        this.filterValue = "";
        this.applyFilter();
    }

    queueStateSave() {
        if (this.stateSaveTimer !== false) {
            clearTimeout(this.stateSaveTimer);
        }

        this.stateSaveTimer = setTimeout(async () => {
            this.stateSaveTimer = false;
            if (await this.moduleManager.saveState('tableState')) {
                this.moduleManager.requestPublishNeeded();
            }
        }, 500);
    }

    /**
     * Retrieves the table state and saves it
     */
    updateTableState() {
        if (!this.gridApi || this.stateRestoreInProgress)
            return;

        // generate state from grid
        const tableState = this.agGridHelperService.getGridState(this.gridApi);
        if (tableState != null) {
            this.tableState = tableState;
        }

        // tell module manager to save the state
        this.queueStateSave();
    }

    restoreColumnState() {
        if (!this.tableState || !this.gridApi) {
            return;
        }

        this.agGridHelperService.loadGridState(this.gridApi, this.tableState);
    }

    legendsDisplay() {
        const dispositionCounts = this.data.map(item => ({
            name: item.Name,
            count: item.Handled
        }));
        dispositionCounts.sort((a, b) => b.count - a.count);

        const maxTopDispositions = 5;
        let topDispositions = [];
        let otherCount = 0;
        let isEqual = false;

        if (dispositionCounts.length > maxTopDispositions) {
            const fifthCount = dispositionCounts[maxTopDispositions - 1].count;
            const sixthCount = dispositionCounts[maxTopDispositions].count;
            isEqual = fifthCount === sixthCount;
        }

        if (isEqual) {
            topDispositions = dispositionCounts.slice(0, maxTopDispositions - 1);
            otherCount = dispositionCounts.slice(maxTopDispositions - 1).reduce((acc, curr) => acc + curr.count, 0);
            topDispositions.push({ name: 'Others', count: otherCount });
        } else {
            topDispositions = dispositionCounts.slice(0, maxTopDispositions);
            otherCount = dispositionCounts.slice(maxTopDispositions).reduce((acc, curr) => acc + curr.count, 0);
            if (otherCount > 0) {
                topDispositions.push({ name: 'Others', count: otherCount });
            }
        }

        const labelName = topDispositions.map(disposition => disposition.name);
        const dataForChart = topDispositions.map(disposition => disposition.count);

        this.dataForChart = dataForChart;
        this.labelName = labelName;

        this.dynamicColors = labelName.reduce((acc, label, index) => {
            acc[label] = label === 'Others'
                ? this.fixedOtherColor['Others']
                : this.dispositionColors[index] || this.fixedOtherColor['Others'];
            return acc;
        }, {});
    }

    async loadData(isAutoRefresh: boolean) {
        if (this.isDataLoading)
            return;
        this.isDataLoading = true;

        this.moduleManager.showLoader = !isAutoRefresh;
        try {
            const response = await this.dispositionsService.loadData(this.moduleKey, isAutoRefresh, this.isFirstLoad);
            this.isFirstLoad = false;
            this.moduleManager.setAutoRefreshReady();
            this.data = response.DispositionsRows;
            this.data.sort((a, b) => b.Percentage - a.Percentage);
            this.legendsDisplay();

            if (this.gridApi) {
                this.gridApi.setGridOption('rowData', this.data);
            }
            this.displayTableTotal();
            this.updateTotal();
            this.setupChart();
            this.resetLegendVisibility();
        } catch (e) {
            this.moduleManager.handleLoadError(e, isAutoRefresh);
        } finally {
            this.applyFilter();
            this.isDataLoading = false;
            this.noData = !this.data || this.data.length === 0;
            this.dataLoadSuccess = !this.noData;
            this.moduleManager.showLoader = false;
        }

        this.moduleManager.moduleOptions.nameSuffix = ` (${this.data?.length || 0})`;
    }

    displayTableTotal() {
        let data = [];

        this.gridApi?.forEachNodeAfterFilter((node) => {
            let nodeData = { ...node.data };
            nodeData.Percentage = parseFloat((nodeData.Percentage * 100).toFixed(2));
            data.push(nodeData);
        });

        const total = data.reduce((sum, item) => sum + item.Handled, 0);
        const totalPercentage = data.reduce((sum, item) => sum + item.Percentage, 0);

        // Convert AHT and AHTDFO to seconds, sum them, and convert back to HH:MM:SS
        const totalAHTSeconds = data.reduce((sum, item) => sum + this.timeToSeconds(item.AHT), 0);
        const totalAHT = this.secondsToTime(totalAHTSeconds);
        const pinnedBottomRowData = data.length > 0 ? [{
            Name: this.cvLocalizationService.getCachedText("Dispositions_List", "Total", "Total"),
            Handled: total,
            Percentage: totalPercentage,
            AHT: totalAHT
        }] : null;

        this.gridApi?.setGridOption('pinnedBottomRowData', pinnedBottomRowData);
    }

    private timeToSeconds(time: string): number {
        const [hours, minutes, seconds] = time.split(':').map(Number);
        return hours * 3600 + minutes * 60 + seconds;
    }

    private secondsToTime(seconds: number): string {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return [hours, minutes, secs].map(val => val < 10 ? `0${val}` : val).join(':');
    }

    /**
     * Shows and hides the search/filter bar
     */
    private toggleOpenSearch() {
        if (this.showFilter) {
            this.showFilter = false;
            this.gridApi?.setGridOption('suppressNoRowsOverlay', true);
            this.clearFilter();
        } else {
            this.showFilter = true;
            this.gridApi?.setGridOption('suppressNoRowsOverlay', false);
            this.focusOnSearch();
        }
    }

    /**
     * Change input focus to the search bar.
     */
    async focusOnSearch() {
        // The element won't be ready immediately, so the focus has to be done asynchronously
        setTimeout(() => {
            const el = document.querySelector(`div.cxone-text-input input[name='cxoneInput-${this.moduleKey}']`);
            $(el).focus();
        }, 200);
    }

    updateTotal() {
        this.total = this.dataForChart.reduce((sum, value) => sum + value, 0);
        this.setupChart();
    }

    setupChart() {
        const fixedLegendLabels = this.labelName;
        let fixedLegendObj = fixedLegendLabels?.map((label, index) => ({
            name: label,
            y: this.dataForChart[index] || null,
            color: this.getColorForLabel(label),
        }));

        const total = this.dataForChart.reduce((sum, value) => sum + value, 0);

        const tooltipLocalization = this.cvLocalizationService.getCachedText('Dispositions_List', 'Handled_Count', 'Handled count: ')

        this.chartConfig = {
            chart: {
                type: 'pie',
                backgroundColor: 'transparent',
                animation: false,
                spacingBottom: 0,
                spacingTop: 0,
                events: {
                    load: (event) => this.onChartLoad(event),
                    render: (event) => this.onChartRender(event)
                }
            },
            title: {
                text: '',
            },
            tooltip: {
                enabled: true,
                useHTML: true,
                formatter: function () {
                    return `
                        <div style="display: flex; align-items: center;">
                            <div style="width: 12px; height: 12px; background-color: ${this.point.color}; border-radius: 50%; margin-right: 5px;"></div>
                            <div style="font-weight: bold;">${this.point.name}</div>
                        </div>
                        <div style="font-weight: normal; padding-top:5px;">${tooltipLocalization} <span style="font-weight: bold;">${this.point.y}</span></div>
                    `;
                },
                backgroundColor: '#FFF',
                borderColor: '#D0D2D3',
                style: {
                    color: '#000',
                    fontFamily: 'Open Sans',
                    fontSize: '13px',
                    fontWeight: '400',
                    lineHeight: '20px',
                },
            },
            plotOptions: {
                pie: {
                    animation: false,
                    allowPointSelect: false,
                    states: {
                        hover: {
                            enabled: false
                        },
                        inactive: {
                            enabled: false,
                            opacity: 1
                        }
                    },
                    dataLabels: {
                        enabled: true,
                        crop: false,
                        formatter: function () {
                            const total = this.series['yData'].reduce((sum, value) => sum + value, 0);
                            const percentage = (this.point.y / total) * 100;
                            const threshold = 5;

                            const width = this.series.chart.plotWidth;
                            const height = this.series.chart.plotHeight;
                            const dataLabelFontSize = Math.min(width, height) * 0.07;

                            const darkColors = ['#4959A4', '#6A46A4'];
                            const pointColor = this.point.color as string;
                            const textColor = darkColors.includes(pointColor) ? '#FFFFFF' : '#000000';

                            return (percentage < threshold) ? '' : `<span style="font-size: ${dataLabelFontSize}px; color: ${textColor};">${this.point.y}</span>`;
                        },
                        distance: '-20%',
                        y: -4,
                        style: {
                            fontWeight: '400',
                            fontSize: '16px',
                            fontFamily: 'Open Sans',
                            color: '#000000'
                        },
                        connectorWidth: 0
                    },
                    showInLegend: true,
                    innerSize: '60%',
                    point: {
                        events: {
                            legendItemClick: (event) => this.onLegendClick(event)
                        }
                    }
                },
            },
            legend: {
                layout: 'horizontal',
                align: 'center',
                verticalAlign: 'bottom',
                symbolHeight: 12,
                symbolWidth: 12,
                symbolPadding: 4,
                symbolRadius: 0,
                itemDistance: 6,
                itemMarginTop: 5,
                itemMarginBottom: 5,
                itemHiddenStyle: {
                    color: 'rgb(0, 0, 0)'
                },
                itemHoverStyle: {
                    color: 'rgb(0, 0, 0)'
                },
                itemStyle: {
                    fontSize: '12px',
                    fontfamily: 'Inter',
                    fontWeight: '400',
                    color: '#000',
                    lineHeight: '14.52px',
                    cursor: 'default',
                    textOverflow: 'ellipsis'
                },
                labelFormatter: function () {
                    const fixedFontSize = 12;
                    return `<span style="font-size: ${fixedFontSize}px;">${this.name}</span>`;
                },
            },
            series: [{
                name: 'Data',
                type: 'pie',
                data: fixedLegendObj,
            }]
        };

        this.chartConfig.legend = {
            ...this.chartConfig.legend,
            ...this.getLegendConfig(this.moduleState.legendPosition),
            ...this.legendResizeValues
        };

        if (this.highChartObject) {
            this.highChartObject.series[0].remove(false);
            this.highChartObject.addSeries({
                name: 'Data',
                type: 'pie',
                data: fixedLegendObj,
            }, true);
        }
    }

    updateCustomLabel = (chart, total) => {
        var textX = chart.plotWidth / 2 + chart.plotLeft;
        var textY = chart.plotHeight / 2 + chart.plotTop;
        const largeFontSize = Math.min(chart.plotWidth, chart.plotHeight) * 0.1;
        const smallFontSize = Math.min(chart.plotWidth, chart.plotHeight) * 0.06;

        if (chart.customLabel1) {
            chart.customLabel1.destroy();
        }
        if (chart.customLabel2) {
            chart.customLabel2.destroy();
        }

        chart.customLabel1 = chart.renderer.text(
            total,
            textX,
            textY
        ).css({
            'color': '#000000',
            'fontSize': largeFontSize + 'px',
            'lineHeight': '12px',
            'fontWeight': 600,
            'font-family': 'Open Sans',
            'textAlign': 'center'
        }).attr({
            align: 'center'
        }).add();

        chart.customLabel2 = chart.renderer.text(
            this.cvLocalizationService.getCachedText("Dispositions_List", "Total_Handled", "Total Handled"),
            textX,
            textY + 20
        ).css({
            'color': '#000000',
            'fontSize': smallFontSize + 'px',
            'lineHeight': '14px',
            'fontWeight': 400,
            'font-family': 'Open Sans',
            'textAlign': 'center'
        }).attr({
            align: 'center'
        }).add();

        var bbox1 = chart.customLabel1.getBBox();
        var bbox2 = chart.customLabel2.getBBox();

        chart.customLabel1.attr({
            y: textY - bbox1.height / 4
        });
        chart.customLabel2.attr({
            y: textY + bbox2.height
        });
    }

    resetLegendVisibility() {
        const chart = this.highChartObject;
        if (chart) {
            chart.series.forEach(series => {
                series.points.forEach(point => {
                    point.update({ visible: true } as Highcharts.PointOptionsObject, false);
                });
            });
        }
    }

    onLegendClick(event) {
        const selectedPoint = event.target as Highcharts.Point;
        selectedPoint.setVisible(selectedPoint.visible ? false : true);
        return false;
    }

    onChartLoad(event) {
        const chart = event.target;

        Highcharts.wrap(chart.legend, 'renderItem', function (proceed, item) {
            proceed.call(this, item);

            if (item.legendItem) {
                if (!item.originalColor) {
                    item.originalColor = item.color;
                }

                if (item.visible) {
                    item.legendItem.css({
                        textDecoration: 'none'
                    });
                } else {
                    item.legendItem.css({
                        textDecoration: 'line-through'
                    });
                }

                if (item.legendSymbol) {
                    item.legendSymbol.attr({
                        fill: item.originalColor || item.color
                    });
                }
            }
        });
    }

    onChartRender(event) {
        const chart = event.target;
        const totalVisible = chart.series[0].data.reduce((sum, point) => sum + (point.visible ? point.y : 0), 0);
        this.updateCustomLabel(chart, totalVisible);
    }

    resizeLegends() {
        const legendPosition = this.highChartObject.legend.options.verticalAlign;
        const chartHeight = this.highChartObject.chartHeight;
        let chartObject = {};

        if (legendPosition === 'top' || legendPosition === 'bottom') {
            const legendMaxHeight = chartHeight * 0.25;
            this.legendResizeValues = {
                maxHeight: legendMaxHeight,
                width: '90%'
            };
        } else {
            const legendMaxHeight = chartHeight * 0.9;
            this.legendResizeValues = {
                maxHeight: legendMaxHeight,
                width: '40%'
            };
        }

        chartObject['legend'] = this.legendResizeValues;
        this.highChartObject.update(chartObject, true);
    }

    getLegendConfig(position: string): Highcharts.LegendOptions {
        let legendOptions: Highcharts.LegendOptions;

        switch (position) {
            case 'top':
                legendOptions = {
                    layout: 'horizontal',
                    align: 'center',
                    verticalAlign: 'top',
                };
                break;
            case 'bottom':
                legendOptions = {
                    layout: 'horizontal',
                    align: 'center',
                    verticalAlign: 'bottom',
                };
                break;
            case 'left':
                legendOptions = {
                    layout: 'vertical',
                    align: 'left',
                    verticalAlign: 'middle',
                };
                break;
            case 'right':
                legendOptions = {
                    layout: 'vertical',
                    align: 'right',
                    verticalAlign: 'middle',
                };
                break;
            default:
                legendOptions = {
                    layout: 'horizontal',
                    align: 'center',
                    verticalAlign: 'top',
                };
                break;
        }

        return legendOptions;
    }

    getColorForLabel(label: string): string {
        return this.dynamicColors[label] || this.fixedOtherColor['Others'];
    }
}
